/**
 * Planning canonical queries (Doc 15). Server-authoritative, deterministic.
 * One predicate per question; UI must not recompute.
 */

import { getSupabaseClient } from './supabaseClient';
import { uncoveredCommitmentItems, type TermCoverageRecord } from './commitmentTerms';
import { CHECK_FAILURE_LABELS, normalizeCheckKey } from './planningConstraintKeys';
import {
  countItemsWithRankProblems,
  describeRankProblems,
  RANKS_NOT_FINAL_FIX,
  type PlanRankProblemRow,
} from './planRankProblems';
import type {
  WorkUnit,
  Initiative,
  DependencyLink,
  WorkUnitReadinessState,
} from '../types/planning';

const TABLE_WORK_UNITS = 'work_units';
const TABLE_INITIATIVES = 'initiatives';
const TABLE_DEPENDENCY_LINKS = 'dependency_links';

const TABLE_CAPACITY_ALLOCATIONS = 'capacity_allocations';
const TABLE_ASSUMPTIONS = 'assumptions';

/** Blocker classification for prescribed CTAs (Doc 17). */
export type BlockerType = 'dependency' | 'capacity' | 'assumption_invalidated' | 'structural';

/** Structured blocker with type and optional blocking work unit for CTA. */
export interface StructuredBlocker {
  reason: string;
  type: BlockerType;
  blocking_work_unit_id?: string;
}

/** Per WorkUnit readiness result (Doc 15 §2, §7). Includes structured_blockers for CTAs. */
export interface WorkUnitReadinessResult {
  work_unit_id: string;
  ready: boolean;
  blockers: string[];
  structured_blockers?: StructuredBlocker[];
}

/** DependenciesSatisfied: for each link into this WorkUnit, from-WU state must match dependency_type (Doc 15 §2.3). Returns blocking from_work_unit_id for CTA. */
function dependenciesSatisfied(
  workUnitId: string,
  links: DependencyLink[],
  fromStateById: Map<string, WorkUnitReadinessState>
): { satisfied: boolean; blockers: string[]; blocking_work_unit_ids: string[] } {
  const blockers: string[] = [];
  const blockingIds: string[] = [];
  const intoThis = links.filter((l) => l.to_work_unit_id === workUnitId);
  for (const link of intoThis) {
    const fromState = fromStateById.get(link.from_work_unit_id) ?? 'not_ready';
    let ok = false;
    switch (link.dependency_type) {
      case 'FS':
        ok = fromState === 'done';
        if (!ok) {
          blockers.push(`Prerequisite must be done (FS; current: ${fromState})`);
          blockingIds.push(link.from_work_unit_id);
        }
        break;
      case 'SS':
        ok = fromState === 'in_progress' || fromState === 'done';
        if (!ok) {
          blockers.push(`Prerequisite must be in progress or done (SS; current: ${fromState})`);
          blockingIds.push(link.from_work_unit_id);
        }
        break;
      case 'FF':
        ok = fromState === 'done';
        if (!ok) {
          blockers.push(`Prerequisite must be done (FF; current: ${fromState})`);
          blockingIds.push(link.from_work_unit_id);
        }
        break;
      case 'SF':
        ok = fromState === 'in_progress';
        if (!ok) {
          blockers.push(`Prerequisite must be in progress (SF; current: ${fromState})`);
          blockingIds.push(link.from_work_unit_id);
        }
        break;
    }
    if (!ok) return { satisfied: false, blockers, blocking_work_unit_ids: blockingIds };
  }
  return { satisfied: true, blockers: [], blocking_work_unit_ids: [] };
}

/** Prescribed CTA for a blocker type (Doc 17). */
export function getBlockerCta(
  type: BlockerType,
  options?: { cycleId?: string; blockingWorkUnitId?: string }
): { label: string; route: string } {
  const cycleId = options?.cycleId ?? '';
  const base = '/planning';
  switch (type) {
    case 'dependency':
      return {
        label: 'Go to blocking Work Unit',
        route: options?.blockingWorkUnitId
          ? `${base}/work-graph?cycleId=${cycleId}&workUnitId=${options.blockingWorkUnitId}`
          : `${base}/work-graph?cycleId=${cycleId}`,
      };
    case 'capacity':
      return { label: 'View capacity', route: `${base}/capacity?cycleId=${cycleId}` };
    case 'assumption_invalidated':
      return { label: 'Review assumptions', route: `${base}/assumptions-risks?cycleId=${cycleId}` };
    case 'structural':
    default:
      return { label: 'Review plan', route: `${base}/plans/${cycleId}` };
  }
}

/**
 * Compute readiness for all WorkUnits in a PlanVersion (Doc 15 §2).
 * Deterministic: DependenciesSatisfied, CapacityFeasible, AssumptionsValid, InitiativeNotDropped.
 */
export async function getWorkUnitReadiness(
  planVersionId: string,
): Promise<{
  data: WorkUnitReadinessResult[] | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data: initRows } = await supabase
      .from(TABLE_INITIATIVES)
      .select('id')
      .eq('plan_version_id', planVersionId);
    const initiativeIds = (initRows ?? []).map((r: { id: string }) => r.id);
    if (initiativeIds.length === 0) return { data: [], error: null };

    const [wuRes, initRes, linkRes, capRes, assumpRes] = await Promise.all([
      supabase.from(TABLE_WORK_UNITS).select('*').in('initiative_id', initiativeIds),
      supabase.from(TABLE_INITIATIVES).select('*').eq('plan_version_id', planVersionId),
      supabase.from(TABLE_DEPENDENCY_LINKS).select('*'),
      supabase.from(TABLE_CAPACITY_ALLOCATIONS).select('id').eq('plan_version_id', planVersionId),
      supabase.from(TABLE_ASSUMPTIONS).select('id, status').eq('plan_version_id', planVersionId),
    ]);

    const workUnits = (wuRes.data ?? []) as WorkUnit[];
    const initiatives = (initRes.data ?? []) as Initiative[];
    // Each link is evaluated by its stored timing type (DEP-018, kept 2026-10-14).
    const links = (linkRes.data ?? []) as DependencyLink[];
    // "Does this plan have capacity" has to be the same question the capacity
    // feasibility check asks, or the two disagree about the same plan. Capacity
    // allocated by hand lands in capacity_allocations; capacity inherited from
    // the committing scenario lands in the buckets' supply windows. Either is
    // capacity. Reading only the first told a plan that had inherited its
    // scenario's supply that it had none.
    let hasCapacity = (capRes.data ?? []).length > 0;
    if (!hasCapacity) {
      const { data: bucketRows } = await supabase
        .from('capability_buckets')
        .select('id')
        .eq('plan_version_id', planVersionId);
      const bucketIds = ((bucketRows ?? []) as { id: string }[]).map((b) => b.id);
      if (bucketIds.length > 0) {
        const { data: windows } = await supabase
          .from('bucket_capacity_windows')
          .select('capacity_hours')
          .in('capability_bucket_id', bucketIds);
        hasCapacity = ((windows ?? []) as { capacity_hours: number | null }[]).some(
          (w) => Number(w.capacity_hours) > 0,
        );
      }
    }
    const hasInvalidAssumption = (assumpRes.data ?? []).some((a: { status: string }) => a.status === 'invalidated');

    const initiativeById = new Map(initiatives.map((i) => [i.id, i]));
    const _wuById = new Map(workUnits.map((w) => [w.id, w]));

    const _initiativeIdByWuId = new Map(workUnits.map((w) => [w.id, w.initiative_id]));

    const results: WorkUnitReadinessResult[] = [];
    const fromStateById = new Map<string, WorkUnitReadinessState>();

    const topologicalOrder = topologicalSort(workUnits, links);
    for (const wu of topologicalOrder) {
      const blockers: string[] = [];
      const structured: StructuredBlocker[] = [];
      fromStateById.set(wu.id, wu.readiness_state);

      const init = initiativeById.get(wu.initiative_id);
      if (!init || init.status === 'dropped') {
        blockers.push('Initiative is dropped');
        structured.push({ reason: 'Initiative is dropped', type: 'structural' });
        results.push({ work_unit_id: wu.id, ready: false, blockers, structured_blockers: structured });
        continue;
      }

      const dep = dependenciesSatisfied(wu.id, links, fromStateById);
      if (!dep.satisfied) {
        blockers.push(...dep.blockers);
        dep.blocking_work_unit_ids.forEach((id, i) => {
          structured.push({
            reason: dep.blockers[i] ?? 'Dependency not satisfied',
            type: 'dependency',
            blocking_work_unit_id: id,
          });
        });
        results.push({ work_unit_id: wu.id, ready: false, blockers, structured_blockers: structured });
        continue;
      }
      if (!hasCapacity) {
        blockers.push('No capacity allocation for plan');
        structured.push({ reason: 'No capacity allocation for plan', type: 'capacity' });
        results.push({ work_unit_id: wu.id, ready: false, blockers, structured_blockers: structured });
        continue;
      }
      if (hasInvalidAssumption) {
        blockers.push('An assumption is invalidated');
        structured.push({ reason: 'An assumption is invalidated', type: 'assumption_invalidated' });
        results.push({ work_unit_id: wu.id, ready: false, blockers, structured_blockers: structured });
        continue;
      }

      results.push({
        work_unit_id: wu.id,
        ready: true,
        blockers: [],
      });
    }

    for (const wu of workUnits) {
      if (results.some((r) => r.work_unit_id === wu.id)) continue;
      const dep = dependenciesSatisfied(wu.id, links, fromStateById);
      const init = initiativeById.get(wu.initiative_id);
      const blockers: string[] = [];
      const structured: StructuredBlocker[] = [];
      if (!init || init.status === 'dropped') {
        blockers.push('Initiative dropped');
        structured.push({ reason: 'Initiative dropped', type: 'structural' });
      }
      if (!dep.satisfied) {
        blockers.push(...dep.blockers);
        dep.blocking_work_unit_ids.forEach((id, i) => {
          structured.push({
            reason: dep.blockers[i] ?? 'Dependency not satisfied',
            type: 'dependency',
            blocking_work_unit_id: id,
          });
        });
        if (structured.length === 0) dep.blockers.forEach((b) => structured.push({ reason: b, type: 'dependency' }));
      }
      if (!hasCapacity) {
        blockers.push('No capacity allocation');
        structured.push({ reason: 'No capacity allocation', type: 'capacity' });
      }
      if (hasInvalidAssumption) {
        blockers.push('Invalidated assumption');
        structured.push({ reason: 'Invalidated assumption', type: 'assumption_invalidated' });
      }
      if (blockers.length === 0) {
        blockers.push('Not ready');
        structured.push({ reason: 'Not ready', type: 'structural' });
      }
      results.push({
        work_unit_id: wu.id,
        ready: false,
        blockers,
        structured_blockers: structured,
      });
    }

    return { data: results, error: null };
  } catch (e) {
    return {
      data: null,
      error: e instanceof Error ? e.message : 'Failed to compute readiness',
    };
  }
}

function topologicalSort(workUnits: WorkUnit[], links: DependencyLink[]): WorkUnit[] {
  const inDegree = new Map<string, number>();
  const outEdges = new Map<string, string[]>();
  for (const w of workUnits) {
    inDegree.set(w.id, 0);
    outEdges.set(w.id, []);
  }
  for (const l of links) {
    if (workUnits.some((w) => w.id === l.from_work_unit_id) && workUnits.some((w) => w.id === l.to_work_unit_id)) {
      inDegree.set(l.to_work_unit_id, (inDegree.get(l.to_work_unit_id) ?? 0) + 1);
      outEdges.get(l.from_work_unit_id)!.push(l.to_work_unit_id);
    }
  }
  const queue = workUnits.filter((w) => inDegree.get(w.id) === 0).map((w) => w.id);
  const order: string[] = [];
  while (queue.length > 0) {
    const id = queue.shift()!;
    order.push(id);
    for (const toId of outEdges.get(id) ?? []) {
      let d = inDegree.get(toId) ?? 0;
      d--;
      inDegree.set(toId, d);
      if (d === 0) queue.push(toId);
    }
  }
  const idToWu = new Map(workUnits.map((w) => [w.id, w]));
  return order.map((id) => idToWu.get(id)!).filter(Boolean);
}

/**
 * Critical path: longest path in dependency DAG by duration_score (Doc 15 §3).
 * Returns set of work_unit_ids on the critical path. CriticalPathFactor = 1.5 for these, 1.0 else.
 */
export async function getCriticalPathWorkUnitIds(planVersionId: string): Promise<{
  data: Set<string> | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data: initIds } = await supabase
      .from(TABLE_INITIATIVES)
      .select('id')
      .eq('plan_version_id', planVersionId);
    const initiativeIds = (initIds ?? []).map((r: { id: string }) => r.id);
    if (initiativeIds.length === 0) return { data: new Set(), error: null };

    const { data: wuRows } = await supabase
      .from(TABLE_WORK_UNITS)
      .select('id, initiative_id')
      .in('initiative_id', initiativeIds);
    const workUnits = (wuRows ?? []) as { id: string; initiative_id: string }[];
    const { data: linkRows } = await supabase.from(TABLE_DEPENDENCY_LINKS).select('from_work_unit_id, to_work_unit_id');
    const links = (linkRows ?? []) as { from_work_unit_id: string; to_work_unit_id: string }[];
    const { data: fullWu } = await supabase
      .from(TABLE_WORK_UNITS)
      .select('id, duration_score')
      .in('id', workUnits.map((w) => w.id));
    const durationById = new Map((fullWu ?? []).map((w: { id: string; duration_score: number | null }) => [w.id, Math.max(0, w.duration_score ?? 1)]));

    const wuIds = new Set(workUnits.map((w) => w.id));
    const filteredLinks = links.filter((l) => wuIds.has(l.from_work_unit_id) && wuIds.has(l.to_work_unit_id));

    const order = topologicalSort(workUnits as WorkUnit[], filteredLinks as DependencyLink[]);
    const earliestFinish = new Map<string, number>();
    for (const w of order) {
      const duration = durationById.get(w.id) ?? 1;
      const preds = filteredLinks.filter((l) => l.to_work_unit_id === w.id).map((l) => l.from_work_unit_id);
      const maxPred = preds.length ? Math.max(...preds.map((id) => earliestFinish.get(id) ?? 0)) : 0;
      earliestFinish.set(w.id, maxPred + duration);
    }
    const projectLength = Math.max(...Array.from(earliestFinish.values()), 0);
    const latestStart = new Map<string, number>();
    for (let i = order.length - 1; i >= 0; i--) {
      const w = order[i];
      const duration = durationById.get(w.id) ?? 1;
      const succs = filteredLinks.filter((l) => l.from_work_unit_id === w.id).map((l) => l.to_work_unit_id);
      const minSucc = succs.length ? Math.min(...succs.map((id) => latestStart.get(id)! - duration)) : projectLength - duration;
      latestStart.set(w.id, minSucc);
    }
    const criticalPathIds = new Set<string>();
    for (const w of order) {
      const duration = durationById.get(w.id) ?? 1;
      const ef = earliestFinish.get(w.id) ?? 0;
      const ls = latestStart.get(w.id) ?? 0;
      if (Math.abs(ef - (ls + duration)) < 1e-6) criticalPathIds.add(w.id);
    }
    return { data: criticalPathIds, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to compute critical path' };
  }
}

/** Next Right WorkUnit candidate with score and explanation (Doc 15 §6). */
export interface NextRightWorkUnitItem {
  work_unit_id: string;
  title: string;
  initiative_id: string;
  priority_position: number | null;
  score: number;
  top_positive_drivers: string[];
  top_penalties: string[];
  blocking_reason: string | null;
  /** Predecessor work unit titles (what must stay done for this to remain ready). */
  predecessor_titles: string[];
}

/**
 * Next Right WorkUnit (v0): candidates = ready, initiative not dropped; score by Doc 15 §6.
 */
export async function getNextRightWorkUnits(
  planCycleId: string,
  options?: { limit?: number }
): Promise<{ data: NextRightWorkUnitItem[] | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const limit = options?.limit ?? 10;

    const { data: por } = await supabase
      .from('plan_of_record')
      .select('plan_version_id')
      .eq('plan_cycle_id', planCycleId)
      .eq('status', 'active')
      .maybeSingle();
    if (!por?.plan_version_id) return { data: [], error: null };

    const planVersionId = por.plan_version_id as string;
    const [readinessRes, criticalPathRes] = await Promise.all([
      getWorkUnitReadiness(planVersionId),
      getCriticalPathWorkUnitIds(planVersionId),
    ]);
    if (readinessRes.error) return { data: null, error: readinessRes.error };
    const criticalPathIds = criticalPathRes.data ?? new Set<string>();

    const { data: initIds2 } = await supabase
      .from(TABLE_INITIATIVES)
      .select('id')
      .eq('plan_version_id', planVersionId);
    const initiativeIds2 = (initIds2 ?? []).map((r: { id: string }) => r.id);
    const { data: workUnits } = await supabase
      .from(TABLE_WORK_UNITS)
      .select('*')
      .in('initiative_id', initiativeIds2);
    const { data: initiatives } = await supabase
      .from(TABLE_INITIATIVES)
      .select('*')
      .eq('plan_version_id', planVersionId);

    const wuList = (workUnits ?? []) as WorkUnit[];
    const initList = (initiatives ?? []) as Initiative[];
    const initById = new Map(initList.map((i) => [i.id, i]));
    const readySet = new Set(
      (readinessRes.data ?? []).filter((r) => r.ready).map((r) => r.work_unit_id)
    );

    const candidates = wuList.filter((wu) => {
      const init = initById.get(wu.initiative_id);
      return init && init.status !== 'dropped' && readySet.has(wu.id);
    });

    const dependentCount = new Map<string, number>();
    for (const wu of wuList) {
      dependentCount.set(wu.id, 0);
    }
    const { data: linkRows } = await supabase.from(TABLE_DEPENDENCY_LINKS).select('from_work_unit_id, to_work_unit_id');
    const linksList = (linkRows ?? []) as { from_work_unit_id: string; to_work_unit_id: string }[];
    for (const l of linksList) {
      dependentCount.set(l.from_work_unit_id, (dependentCount.get(l.from_work_unit_id) ?? 0) + 1);
    }
    const wuById = new Map(wuList.map((w) => [w.id, w]));
    const predecessorsByToId = new Map<string, string[]>();
    for (const l of linksList) {
      const fromTitle = wuById.get(l.from_work_unit_id)?.title;
      if (!fromTitle) continue;
      const arr = predecessorsByToId.get(l.to_work_unit_id) ?? [];
      arr.push(fromTitle);
      predecessorsByToId.set(l.to_work_unit_id, arr);
    }

    const maxPriority = Math.max(...initList.map((i) => i.priority_position ?? 0), 1);
    const items: NextRightWorkUnitItem[] = candidates.map((wu) => {
      const init = initById.get(wu.initiative_id)!;
      const priorityPos = init.priority_position ?? 0;
      const priorityWeight = maxPriority > 0 ? (maxPriority - priorityPos + 1) / maxPriority : 1;
      const criticalPathFactor = criticalPathIds.has(wu.id) ? 1.5 : 1.0;
      const uncertaintyScore = Math.min(1, Math.max(0, wu.uncertainty_score ?? 0));
      const depCount = dependentCount.get(wu.id) ?? 0;
      const uncertaintyReduction = uncertaintyScore * (1 + depCount * 0.2);
      const scheduleSensitivity = 1.0;
      const skillFitScore = 1.0;
      const coordinationPenalty = Math.min(1, Math.max(0, wu.coordination_load_score ?? 0)) * 0.5;
      const riskPenalty = Math.min(1, Math.max(0, wu.risk_score ?? 0)) * 0.5;
      const bufferPenalty = Math.min(1, Math.max(0, wu.buffer_score ?? 0)) * 0.2;

      const score =
        priorityWeight * criticalPathFactor * uncertaintyReduction * scheduleSensitivity * skillFitScore -
        (coordinationPenalty + riskPenalty + bufferPenalty);

      const drivers: string[] = [];
      if (priorityWeight > 0.5) drivers.push('Priority');
      if (criticalPathFactor > 1) drivers.push('Critical path');
      if (uncertaintyReduction > 0.5) drivers.push('Unlocks uncertainty');
      const penalties: string[] = [];
      if (coordinationPenalty > 0) penalties.push('Coordination load');
      if (riskPenalty > 0) penalties.push('Risk');
      if (bufferPenalty > 0) penalties.push('Buffer');

      return {
        work_unit_id: wu.id,
        title: wu.title,
        initiative_id: wu.initiative_id,
        priority_position: init.priority_position ?? null,
        score,
        top_positive_drivers: drivers.slice(0, 3),
        top_penalties: penalties.slice(0, 3),
        blocking_reason: null,
        predecessor_titles: predecessorsByToId.get(wu.id) ?? [],
      };
    });

    items.sort((a, b) => b.score - a.score);
    return { data: items.slice(0, limit), error: null };
  } catch (e) {
    return {
      data: null,
      error: e instanceof Error ? e.message : 'Failed to compute next right work units',
    };
  }
}

/**
 * At-risk work unit ids (Doc 17 §10): on critical path and (low buffer or schedule-sensitive).
 * At risk = critical path ∩ (buffer_score null or < 0.5).
 */
export async function getAtRiskWorkUnitIds(planVersionId: string): Promise<{
  data: Set<string> | null;
  error: string | null;
}> {
  try {
    const [cpRes, { data: wuRows }] = await Promise.all([
      getCriticalPathWorkUnitIds(planVersionId),
      getSupabaseClient()
        .from(TABLE_INITIATIVES)
        .select('id')
        .eq('plan_version_id', planVersionId)
        .then((r) => r.data as { id: string }[] | null)
        .then((initIds) => {
          if (!initIds?.length) return { data: [] };
          return getSupabaseClient()
            .from(TABLE_WORK_UNITS)
            .select('id, buffer_score')
            .in('initiative_id', initIds.map((i) => i.id));
        }),
    ]);
    if (cpRes.error || !cpRes.data) return { data: null, error: cpRes.error ?? 'Critical path failed' };
    const workUnits = (wuRows ?? []) as { id: string; buffer_score: number | null }[];
    const atRisk = new Set<string>();
    for (const w of workUnits) {
      if (!cpRes.data.has(w.id)) continue;
      const lowBuffer = w.buffer_score == null || w.buffer_score < 0.5;
      if (lowBuffer) atRisk.add(w.id);
    }
    return { data: atRisk, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to compute at-risk work units' };
  }
}

/**
 * Downstream work unit ids (what slips if this slips?) — transitive successors in dependency graph (Doc 17 §10).
 */
export async function getDownstreamWorkUnitIds(
  planVersionId: string,
  workUnitId: string
): Promise<{ data: string[] | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data: initIds } = await supabase.from(TABLE_INITIATIVES).select('id').eq('plan_version_id', planVersionId);
    const initiativeIds = (initIds ?? []).map((r: { id: string }) => r.id);
    if (initiativeIds.length === 0) return { data: [], error: null };
    const { data: wuRows } = await supabase.from(TABLE_WORK_UNITS).select('id').in('initiative_id', initiativeIds);
    const wuIds = new Set((wuRows ?? []).map((r: { id: string }) => r.id));
    if (!wuIds.has(workUnitId)) return { data: [], error: null };
    const { data: linkRows } = await supabase.from(TABLE_DEPENDENCY_LINKS).select('from_work_unit_id, to_work_unit_id');
    const links = (linkRows ?? []).filter(
      (l: { from_work_unit_id: string; to_work_unit_id: string }) =>
        wuIds.has(l.from_work_unit_id) && wuIds.has(l.to_work_unit_id)
    ) as { from_work_unit_id: string; to_work_unit_id: string }[];
    const succ = new Map<string, Set<string>>();
    for (const l of links) {
      if (!succ.has(l.from_work_unit_id)) succ.set(l.from_work_unit_id, new Set());
      succ.get(l.from_work_unit_id)!.add(l.to_work_unit_id);
    }
    const downstream = new Set<string>();
    const stack = [workUnitId];
    while (stack.length > 0) {
      const cur = stack.pop()!;
      const nextIds = succ.get(cur);
      if (!nextIds) continue;
      for (const id of nextIds) {
        if (downstream.has(id)) continue;
        downstream.add(id);
        stack.push(id);
      }
    }
    return { data: Array.from(downstream), error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to compute downstream work units' };
  }
}

// ---------------------------------------------------------------------------
// Doc 15 — Foundational predicates and POR authorization gate (truth table)
// ---------------------------------------------------------------------------

const TABLE_CAPABILITY_BUCKETS = 'capability_buckets';
const TABLE_BUCKET_ALLOCATIONS = 'bucket_allocations';
const TABLE_PLAN_CONSTRAINT_EVALUATIONS = 'plan_constraint_evaluations';
const TABLE_CONSTRAINT_CHECK_RESULTS = 'constraint_check_results';
const TABLE_RANK_SEQUENCE_DIVERGENCES = 'rank_sequence_divergences';
const TABLE_TRADEOFF_DECISIONS = 'tradeoff_decisions';
const TABLE_REPRIORITIZATION_TRIGGERS = 'reprioritization_triggers';

/** Result of a single predicate (Doc 15 §2–4). */
export interface PredicateResult {
  value: boolean;
  reason?: string;
}

/**
 * BucketsMaterialized(plan_version) — Doc 15 §2.1.
 * True iff capability buckets and bucket allocations exist for the PlanVersion.
 */
export async function bucketsMaterialized(planVersionId: string): Promise<PredicateResult> {
  try {
    const supabase = getSupabaseClient();
    const [buckets, allocs] = await Promise.all([
      supabase.from(TABLE_CAPABILITY_BUCKETS).select('id', { count: 'exact', head: true }).eq('plan_version_id', planVersionId),
      supabase.from(TABLE_BUCKET_ALLOCATIONS).select('id', { count: 'exact', head: true }).eq('plan_version_id', planVersionId),
    ]);
    const bucketCount = buckets.count ?? 0;
    const allocCount = allocs.count ?? 0;
    if (bucketCount === 0) return { value: false, reason: 'No capability buckets materialized for this plan version' };
    if (allocCount === 0) return { value: false, reason: 'No bucket allocations materialized for this plan version' };
    return { value: true };
  } catch (e) {
    return { value: false, reason: e instanceof Error ? e.message : 'Failed to check buckets' };
  }
}

/**
 * EvaluationComputed(plan_version) — Doc 15 §2.2.
 * True iff plan_constraint_evaluations.status is 'computed' or 'acknowledged' for the PlanVersion.
 * 'acknowledged' means the planner has reviewed the computed evaluation — it is still a valid
 * computed evaluation and must not block POR authorization.
 */
export async function evaluationComputed(planVersionId: string): Promise<PredicateResult> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_PLAN_CONSTRAINT_EVALUATIONS)
      .select('id, status, evaluated_at')
      .eq('plan_version_id', planVersionId)
      .in('status', ['computed', 'acknowledged'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) return { value: false, reason: error.message };
    if (data == null) {
      return { value: false, reason: 'Plan health has not been checked for this plan version — open the plan and check plan health first' };
    }

    // Authorization is judged against the LAST evaluation, whenever it was run.
    // Change the plan after that and the verdict on screen describes a plan that
    // no longer exists — which is how capacity could be allocated and the checks
    // still report the state from before it.
    //
    // Only some of what a plan is made of records when it changed: capacity
    // supply windows do, initiatives, work units and capacity allocations carry
    // no timestamps at all, so a change to those is not detectable here. This
    // catches the case it can prove rather than claiming to catch them all.
    const evaluatedAt = (data as { evaluated_at?: string | null }).evaluated_at;
    if (evaluatedAt) {
      const { data: buckets } = await supabase
        .from(TABLE_CAPABILITY_BUCKETS)
        .select('id')
        .eq('plan_version_id', planVersionId);
      const bucketIds = ((buckets ?? []) as { id: string }[]).map((b) => b.id);
      if (bucketIds.length > 0) {
        const { data: newer } = await supabase
          .from('bucket_capacity_windows')
          .select('created_at')
          .in('capability_bucket_id', bucketIds)
          .gt('created_at', evaluatedAt)
          .limit(1);
        if ((newer ?? []).length > 0) {
          return {
            value: false,
            reason: 'This plan\u2019s capacity changed after its last check — run the checks again before authorizing.',
          };
        }
      }
    }

    return { value: true };
  } catch (e) {
    return { value: false, reason: e instanceof Error ? e.message : 'Failed to check evaluation' };
  }
}

/** Dependency graph acyclic check (Doc 15 §2.3). Used by structuralErrorsAbsent; no dependency on planningService. */
async function dependencyGraphAcyclic(planVersionId: string): Promise<{ acyclic: boolean; reason?: string }> {
  const supabase = getSupabaseClient();
  const { data: initRows } = await supabase.from(TABLE_INITIATIVES).select('id').eq('plan_version_id', planVersionId);
  const initiativeIds = (initRows ?? []).map((r: { id: string }) => r.id);
  if (initiativeIds.length === 0) return { acyclic: true };

  const { data: wuRows } = await supabase.from(TABLE_WORK_UNITS).select('id').in('initiative_id', initiativeIds);
  const wuIds = new Set((wuRows ?? []).map((r: { id: string }) => r.id));
  if (wuIds.size === 0) return { acyclic: true };

  const { data: linkRows } = await supabase.from(TABLE_DEPENDENCY_LINKS).select('from_work_unit_id, to_work_unit_id');
  const links = (linkRows ?? []).filter(
    (l: { from_work_unit_id: string; to_work_unit_id: string }) => wuIds.has(l.from_work_unit_id) && wuIds.has(l.to_work_unit_id)
  ) as { from_work_unit_id: string; to_work_unit_id: string }[];

  const inDegree = new Map<string, number>();
  const outEdges = new Map<string, string[]>();
  wuIds.forEach((id) => {
    inDegree.set(id, 0);
    outEdges.set(id, []);
  });
  for (const l of links) {
    inDegree.set(l.to_work_unit_id, (inDegree.get(l.to_work_unit_id) ?? 0) + 1);
    outEdges.get(l.from_work_unit_id)!.push(l.to_work_unit_id);
  }
  const queue = Array.from(wuIds).filter((id) => inDegree.get(id) === 0);
  const order: string[] = [];
  while (queue.length > 0) {
    const id = queue.shift()!;
    order.push(id);
    for (const toId of outEdges.get(id) ?? []) {
      const d = (inDegree.get(toId) ?? 0) - 1;
      inDegree.set(toId, d);
      if (d === 0) queue.push(toId);
    }
  }
  if (order.length !== wuIds.size) return { acyclic: false, reason: 'Circular dependency in work unit graph' };
  return { acyclic: true };
}

/**
 * StructuralErrorsAbsent(plan_version) — Doc 15 §2.3.
 * True iff dependency graph is acyclic and all referenced objects exist within the PlanVersion snapshot.
 */
export async function structuralErrorsAbsent(planVersionId: string): Promise<PredicateResult> {
  const { acyclic, reason } = await dependencyGraphAcyclic(planVersionId);
  return { value: acyclic, reason };
}

/**
 * Constraint check row from DB (result + optional payload.severity, payload.check_type).
 */
interface ConstraintCheckRow {
  id: string;
  check_key: string;
  result: string;
  payload?: {
    severity?: number;
    check_type?: string;
    remediation?: string;
    failure_label?: string;
    /** Work-unit ids the check failed on (the evaluation writes them; see Doc 19). */
    target_refs?: string[];
  } | null;
}

/**
 * The work units a severe check failed on, resolved to the items a person can
 * open. `work_units.initiative_id → initiatives.item_id` is the governance
 * layer's own chain; a unit with no item keeps its own title.
 */
async function resolveSevereCheckTargets(
  supabase: ReturnType<typeof getSupabaseClient>,
  workUnitIds: readonly string[],
): Promise<Map<string, SevereCheckTarget>> {
  const out = new Map<string, SevereCheckTarget>();
  const ids = Array.from(new Set(workUnitIds.filter((id) => typeof id === 'string' && id.length > 0)));
  if (ids.length === 0) return out;
  const { data: units } = await supabase
    .from(TABLE_WORK_UNITS)
    .select('id, title, initiative_id')
    .in('id', ids);
  const unitRows = (units ?? []) as Array<{ id: string; title: string | null; initiative_id: string | null }>;
  const initiativeIds = Array.from(new Set(unitRows.map((u) => u.initiative_id).filter((x): x is string => !!x)));
  const initiativesById = new Map<string, { item_id: string | null; title: string | null }>();
  if (initiativeIds.length > 0) {
    const { data: inits } = await supabase
      .from(TABLE_INITIATIVES)
      .select('id, item_id, title')
      .in('id', initiativeIds);
    for (const row of (inits ?? []) as Array<{ id: string; item_id: string | null; title: string | null }>) {
      initiativesById.set(row.id, { item_id: row.item_id, title: row.title });
    }
  }
  for (const u of unitRows) {
    const init = u.initiative_id ? initiativesById.get(u.initiative_id) : undefined;
    out.set(u.id, {
      workUnitId: u.id,
      itemId: init?.item_id ?? null,
      title: (init?.title ?? u.title ?? '').trim() || 'an unnamed work unit',
    });
  }
  return out;
}

/** Default severity threshold for "severe" violation (Doc 15 §2.4, §6). */
const DEFAULT_SEVERITY_THRESHOLD = 0.5;

/**
 * SevereViolationsPresent(plan_version) — Doc 15 §6 truth table.
 * True iff any constraint_check_results for this plan version have result = 'fail' with severity >= threshold.
 * Uses evaluation for plan_version to load constraint_check_results.
 */
export async function severeViolationsPresent(
  planVersionId: string,
  options?: { severityThreshold?: number }
): Promise<PredicateResult & { severeChecks: SevereCheck[] }> {
  try {
    const supabase = getSupabaseClient();
    const { data: evalRow } = await supabase
      .from(TABLE_PLAN_CONSTRAINT_EVALUATIONS)
      .select('id')
      .eq('plan_version_id', planVersionId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!evalRow?.id) return { value: false, reason: 'No constraint evaluation; cannot determine violations', severeChecks: [] };

    const { data: checks } = await supabase
      .from(TABLE_CONSTRAINT_CHECK_RESULTS)
      .select('id, check_key, result, payload')
      .eq('plan_constraint_evaluation_id', evalRow.id);
    const rows = (checks ?? []) as ConstraintCheckRow[];
    const threshold = options?.severityThreshold ?? DEFAULT_SEVERITY_THRESHOLD;
    const severeRows = rows.filter((r) => {
      if (r.result !== 'fail') return false;
      const severity = r.payload?.severity;
      if (severity == null) return true;
      return severity >= threshold;
    });
    const hasSevere = severeRows.length > 0;
    // Carry WHICH checks failed, what their payload says to do about it, and
    // WHICH ITEMS they failed on. The evaluation has written a remediation and
    // target_refs per violation since Doc 19; the authorization refusal used to
    // name only the remedy for the refusal itself ("record an override"), never
    // the thing that needed remedying, and never the item it was on (§2.8).
    const targets = await resolveSevereCheckTargets(
      supabase,
      severeRows.flatMap((r) => (Array.isArray(r.payload?.target_refs) ? r.payload!.target_refs! : [])),
    );
    const severeChecks: SevereCheck[] = severeRows.map((r) => {
      const key = normalizeCheckKey(r.check_key);
      const remediation = r.payload?.remediation;
      // A check that knows more than its key does says so in failure_label —
      // e.g. DependenciesSatisfied failing because no capacity is allocated.
      const written = r.payload?.failure_label;
      const refs = Array.isArray(r.payload?.target_refs) ? r.payload!.target_refs! : [];
      return {
        key,
        label:
          typeof written === 'string' && written.trim() !== ''
            ? written.trim()
            : CHECK_FAILURE_LABELS[key] ?? key,
        remediation: typeof remediation === 'string' && remediation.trim() !== '' ? remediation.trim() : null,
        targets: refs.map((id) => targets.get(id)).filter((t): t is SevereCheckTarget => t != null),
      };
    });
    return {
      value: hasSevere,
      reason: hasSevere ? 'One or more constraint checks failed with severity above threshold' : undefined,
      severeChecks,
    };
  } catch (e) {
    return { value: false, reason: e instanceof Error ? e.message : 'Failed to check violations', severeChecks: [] };
  }
}

/** The item (or bare work unit) a severe check failed on. */
export interface SevereCheckTarget {
  workUnitId: string;
  /** The item to open; null when the work unit is not item-bound. */
  itemId: string | null;
  title: string;
}

/** One severe violation, named so a person can act on it. */
export interface SevereCheck {
  key: string;
  label: string;
  remediation: string | null;
  /** What it failed on. Empty when the evaluation wrote no target_refs. */
  targets: SevereCheckTarget[];
}

/**
 * "…and here is what is wrong." A refusal that names only its own remedy
 * ("record an override") leaves the person to go hunting for the violation.
 * Names the items first, then the check, then the fix. De-duplicated: the
 * same check can fail on several work units, and they are listed together.
 */
export function describeSevereChecks(checks: readonly SevereCheck[]): string {
  const byKey = new Map<string, { check: SevereCheck; titles: string[] }>();
  for (const c of checks) {
    const entry = byKey.get(c.key) ?? { check: c, titles: [] };
    for (const t of c.targets) if (!entry.titles.includes(t.title)) entry.titles.push(t.title);
    byKey.set(c.key, entry);
  }
  const parts: string[] = [];
  for (const { check, titles } of byKey.values()) {
    const fix = check.remediation?.replace(/\.$/, '') ?? null;
    const what = titles.length > 0 ? `${titles.join(', ')}: ${check.label}` : check.label;
    parts.push(fix ? `${what} — ${fix}` : what);
  }
  return parts.join('; ');
}

/** The rule, said second: the escape hatch for a person who must authorize anyway. */
export const SEVERE_OVERRIDE_RULE =
  'Authorizing anyway requires a governance override (who, what, why, and a link to the evaluation).';

/**
 * OverridesApproved(plan_version) — Doc 15 §6, Doc 14 §5.
 * When evaluationId is provided (severe violations path): true iff there exists a governance override artifact
 * (tradeoff_decision) with who (accepted_by), when (accepted_at), why (payload.reason or payload.why),
 * and link to evaluation (payload.constraint_evaluation_id = evaluationId).
 * When evaluationId not provided: true iff any tradeoff exists (backward compat when no severe violations).
 */
export async function overridesApproved(
  planVersionId: string,
  options?: { evaluationId?: string | null }
): Promise<PredicateResult> {
  try {
    const supabase = getSupabaseClient();
    if (options?.evaluationId) {
      const { data: rows, error } = await supabase
        .from(TABLE_TRADEOFF_DECISIONS)
        .select('id, payload, accepted_by, accepted_at')
        .eq('plan_version_id', planVersionId)
        .not('accepted_by', 'is', null)
        .not('accepted_at', 'is', null);
      if (error) return { value: false, reason: error.message };
      const evalId = options.evaluationId;
      const evalIdStr = String(evalId);
      const hasValidOverride = (rows ?? []).some((r) => {
        const p = (r.payload as Record<string, unknown>) ?? {};
        const reason = p.reason ?? p.why;
        const linkedEval =
          p.constraint_evaluation_id != null && String(p.constraint_evaluation_id) === evalIdStr;
        return linkedEval && reason != null && String(reason).trim() !== '';
      });
      return {
        value: hasValidOverride,
        reason: hasValidOverride ? undefined : 'Severe violations require a governance override artifact (who, what, why, link to evaluation)',
      };
    }
    const { count, error } = await supabase
      .from(TABLE_TRADEOFF_DECISIONS)
      .select('id', { count: 'exact', head: true })
      .eq('plan_version_id', planVersionId);
    if (error) return { value: false, reason: error.message };
    const hasOverride = (count ?? 0) > 0;
    return { value: hasOverride, reason: !hasOverride ? 'No approved overrides for this plan version' : undefined };
  } catch (e) {
    return { value: false, reason: e instanceof Error ? e.message : 'Failed to check overrides' };
  }
}

/**
 * OverrideForUnresolvedDivergence(plan_version) — Doc 14 §5.
 * True iff there exists a governance override artifact (tradeoff with tradeoff_kind 'divergence_override')
 * with who (accepted_by), when (accepted_at), why (payload.reason or payload.why), and links (payload.divergence_ids).
 * When there are no divergences, returns true (N/A). When there are unresolved divergences, requires such an override.
 */
export async function overrideForUnresolvedDivergence(planVersionId: string): Promise<PredicateResult> {
  try {
    const supabase = getSupabaseClient();
    const { data: divRows, error: divErr } = await supabase
      .from(TABLE_RANK_SEQUENCE_DIVERGENCES)
      .select('id')
      .eq('plan_version_id', planVersionId);
    if (divErr) return { value: false, reason: divErr.message };
    const divergenceIds = (divRows ?? []).map((r: { id: string }) => r.id);
    if (divergenceIds.length === 0) return { value: true };

    const { data: tradeoffs, error: tErr } = await supabase
      .from(TABLE_TRADEOFF_DECISIONS)
      .select('id, payload, accepted_by, accepted_at')
      .eq('plan_version_id', planVersionId)
      .eq('tradeoff_kind', 'divergence_override')
      .not('accepted_by', 'is', null)
      .not('accepted_at', 'is', null);
    if (tErr) return { value: false, reason: tErr.message };
    const _setDiv = new Set(divergenceIds);
    const requiredSet = new Set(divergenceIds.map((id) => String(id)));
    const hasValid = (tradeoffs ?? []).some((t) => {
      const p = (t.payload as Record<string, unknown>) ?? {};
      const reason = p.reason ?? p.why;
      const rawIds = p.divergence_ids;
      const ids: string[] = Array.isArray(rawIds) ? rawIds.map((x) => String(x)) : [];
      const payloadSet = new Set(ids);
      const coversAll = requiredSet.size > 0 && requiredSet.size <= payloadSet.size && [...requiredSet].every((id) => payloadSet.has(id));
      return reason != null && String(reason).trim() !== '' && coversAll;
    });
    return {
      value: hasValid,
      reason: hasValid ? undefined : 'Unresolved divergence requires a governance override artifact (who, what, why, link to divergence_ids)',
    };
  } catch (e) {
    return { value: false, reason: e instanceof Error ? e.message : 'Failed to check divergence override' };
  }
}

/**
 * DivergenceDetected(por_or_plan_version) — Doc 15 §4.1.
 * True iff any rank_sequence_divergences exist for the plan version.
 */
export async function divergenceDetected(planVersionId: string): Promise<PredicateResult> {
  try {
    const supabase = getSupabaseClient();
    const { count, error } = await supabase
      .from(TABLE_RANK_SEQUENCE_DIVERGENCES)
      .select('id', { count: 'exact', head: true })
      .eq('plan_version_id', planVersionId);
    if (error) return { value: false, reason: error.message };
    return { value: (count ?? 0) > 0 };
  } catch (e) {
    return { value: false, reason: e instanceof Error ? e.message : 'Failed to check divergence' };
  }
}

/** Resolution status: column (Doc 18 §4.1) or payload (Doc 15 §4.2). */
const RESOLVED_STATUSES = ['accepted', 'mitigated'];

/**
 * DivergenceResolved(por_or_plan_version) — Doc 15 §4.2.
 * True iff every divergence has resolution_status ∈ {accepted, mitigated} (or disputed with governance approval; not modeled here).
 * Uses rank_sequence_divergences.resolution_status column when present, else payload.resolution_status.
 */
export async function divergenceResolved(planVersionId: string): Promise<PredicateResult> {
  try {
    const supabase = getSupabaseClient();
    const { data: rows, error } = await supabase
      .from(TABLE_RANK_SEQUENCE_DIVERGENCES)
      .select('id, payload, resolution_status')
      .eq('plan_version_id', planVersionId);
    if (error) return { value: false, reason: error.message };
    const divergences = (rows ?? []) as {
      id: string;
      payload?: { resolution_status?: string } | null;
      resolution_status?: string | null;
    }[];
    if (divergences.length === 0) return { value: true };
    const allResolved = divergences.every((d) => {
      const status = d.resolution_status ?? d.payload?.resolution_status;
      return status != null && RESOLVED_STATUSES.includes(status);
    });
    return {
      value: allResolved,
      reason: allResolved ? undefined : 'One or more rank/sequence divergences are not accepted or mitigated',
    };
  } catch (e) {
    return { value: false, reason: e instanceof Error ? e.message : 'Failed to check divergence resolution' };
  }
}

/**
 * TermsCoverage(plan_version) — R-1 (ruled 2026-08-24; PRD-scorecard-to-strongest §7).
 * Every non-dropped commitment on the version carries at least one OPEN term
 * (armed | tripped) with a review_by date. Approval without expiry is
 * accumulation by default — but this is a SELF-HEALING gate, not a hard block:
 * porAuthorizable accepts a `terms_coverage_override` tradeoff artifact
 * (who/why/item_ids) in place of coverage, mirroring the divergence-override
 * convention. Attaching terms is the primary remediation; signing an override
 * is the recorded exception (L1/L2/L3).
 */
export async function termsCoverage(planVersionId: string): Promise<
  PredicateResult & { uncoveredItemIds: string[]; planCycleId: string | null }
> {
  try {
    const supabase = getSupabaseClient();
    const { data: pv, error: pvErr } = await supabase
      .from('plan_versions')
      .select('id, plan_cycle_id')
      .eq('id', planVersionId)
      .maybeSingle();
    if (pvErr || !pv) {
      return { value: false, reason: pvErr?.message ?? 'Plan version not found', uncoveredItemIds: [], planCycleId: null };
    }
    const planCycleId = (pv as { plan_cycle_id: string }).plan_cycle_id;

    const { data: inits, error: iErr } = await supabase
      .from(TABLE_INITIATIVES)
      .select('item_id, status')
      .eq('plan_version_id', planVersionId)
      .neq('status', 'dropped')
      .not('item_id', 'is', null);
    if (iErr) return { value: false, reason: iErr.message, uncoveredItemIds: [], planCycleId };
    const itemIds = [...new Set((inits ?? []).map((i: { item_id: string }) => i.item_id))];
    if (itemIds.length === 0) return { value: true, uncoveredItemIds: [], planCycleId };

    const { data: terms, error: tErr } = await supabase
      .from('plan_commitment_terms')
      .select('item_id, status, review_by')
      .eq('plan_cycle_id', planCycleId)
      .in('item_id', itemIds);
    if (tErr) return { value: false, reason: tErr.message, uncoveredItemIds: [], planCycleId };

    const rows: TermCoverageRecord[] = (terms ?? []).map(
      (t: { item_id: string; status: string; review_by: string | null }) => ({
        itemId: t.item_id,
        status: t.status as TermCoverageRecord['status'],
        reviewBy: t.review_by,
      })
    );
    const uncovered = uncoveredCommitmentItems(itemIds, rows);
    return {
      value: uncovered.length === 0,
      reason:
        uncovered.length === 0
          ? undefined
          : `${uncovered.length} commitment(s) carry no stop condition with a review date (R-1)`,
      uncoveredItemIds: uncovered,
      planCycleId,
    };
  } catch (e) {
    return {
      value: false,
      reason: e instanceof Error ? e.message : 'Failed to check terms coverage',
      uncoveredItemIds: [],
      planCycleId: null,
    };
  }
}

/**
 * Governance override artifact for missing terms coverage — same convention as
 * divergence_override: an accepted tradeoff_decision of kind
 * `terms_coverage_override` whose payload carries a non-empty reason and an
 * item_ids array covering every uncovered commitment.
 */
export async function overrideForTermsCoverage(
  planVersionId: string,
  uncoveredItemIds: string[]
): Promise<PredicateResult> {
  try {
    if (uncoveredItemIds.length === 0) return { value: true };
    const supabase = getSupabaseClient();
    const { data: tradeoffs, error } = await supabase
      .from(TABLE_TRADEOFF_DECISIONS)
      .select('id, payload, accepted_by, accepted_at')
      .eq('plan_version_id', planVersionId)
      .eq('tradeoff_kind', 'terms_coverage_override')
      .not('accepted_by', 'is', null)
      .not('accepted_at', 'is', null);
    if (error) return { value: false, reason: error.message };
    const required = new Set(uncoveredItemIds.map(String));
    const hasValid = (tradeoffs ?? []).some((t) => {
      const p = (t.payload as Record<string, unknown>) ?? {};
      const reason = p.reason ?? p.why;
      const rawIds = p.item_ids;
      const ids = new Set(Array.isArray(rawIds) ? rawIds.map((x) => String(x)) : []);
      return (
        reason != null &&
        String(reason).trim() !== '' &&
        [...required].every((id) => ids.has(id))
      );
    });
    return {
      value: hasValid,
      reason: hasValid
        ? undefined
        : 'Commitments without stop conditions require a terms_coverage_override artifact (who, why, item_ids) — or attach terms',
    };
  } catch (e) {
    return { value: false, reason: e instanceof Error ? e.message : 'Failed to check terms-coverage override' };
  }
}

/**
 * RanksAreFinal(plan_version) — owner ruling 2026-10-13, migration 819.
 * A Plan of Record gives every item it commits to one rank, shared with no
 * other. Asks the same database function `authorize_por` refuses on, so the
 * preview and the act cannot disagree. A read failure does not block here —
 * the server is the authority and will refuse with the items named.
 */
export async function ranksAreFinal(planVersionId: string): Promise<
  PredicateResult & { problems: PlanRankProblemRow[] }
> {
  try {
    const { data, error } = await getSupabaseClient().rpc('plan_version_rank_problems', {
      p_plan_version_id: planVersionId,
    });
    if (error) return { value: true, problems: [] };
    const problems = (data ?? []) as PlanRankProblemRow[];
    const clauses = describeRankProblems(problems);
    if (clauses.length === 0) return { value: true, problems: [] };
    return { value: false, reason: `${clauses.join('; ')}. ${RANKS_NOT_FINAL_FIX}`, problems };
  } catch {
    return { value: true, problems: [] };
  }
}

/**
 * Structured blocker for the POR authorization gate (Doc 106 Q-PLAN-015).
 * Each blocker describes a single reason the POR cannot be authorized,
 * with a machine-readable code, a human title, an optional count, and a
 * remediation route for direct navigation.
 */
export interface PorBlocker {
  /** Machine-readable key matching a canonical check constant or divergence type. */
  blocker_code: string;
  /** Short human-readable description for UI display. */
  title: string;
  /** Number of items affected (divergences unresolved, severe violations, etc.). */
  count?: number;
  /** Relative URL the user can navigate to in order to resolve this blocker. */
  remediation_route?: string;
  /** For SEVERE_VIOLATIONS_UNOVERRIDDEN: each failed check with the items it failed on. */
  severe_checks?: SevereCheck[];
}

/** POR authorization gate result (Doc 15 §3.1, §6 truth table; Doc 106 Q-PLAN-015). */
export interface PorAuthorizableResult {
  /** Canonical readiness flag — use this for primary gate checks. */
  ready: boolean;
  /** Structured blockers (non-empty when ready = false). */
  blockers: PorBlocker[];
  /**
   * @deprecated Use `ready` instead. Kept for backward compatibility.
   * Will be removed in a future clean-up pass.
   */
  allowed: boolean;
  /**
   * @deprecated Use `blockers[].title` instead. Kept for backward compatibility.
   */
  errors: string[];
  warnings?: string[];
  /** Predicate values for UI/debug. */
  predicates?: {
    bucketsMaterialized: boolean;
    evaluationComputed: boolean;
    structuralErrorsAbsent: boolean;
    severeViolationsPresent: boolean;
    overridesApproved: boolean;
    divergenceResolved: boolean;
    termsCoverage: boolean;
  };
}

/**
 * PorAuthorizable(plan_version) — Doc 15 §3.1, §6, Doc 14 §5.
 * True iff: BucketsMaterialized, EvaluationComputed, StructuralErrorsAbsent, and
 * (no severe violations OR governance override artifact with who/what/why/link to evaluation), and
 * (DivergenceResolved OR governance override artifact for unresolved divergence with who/what/why/divergence_ids).
 * Authorization fails without required override artifacts when violations or unresolved divergence exist.
 */
export async function porAuthorizable(
  planVersionId: string,
  options?: { includePredicates?: boolean; relaxBucketsAndEvaluationForLegacy?: boolean }
): Promise<PorAuthorizableResult> {
  const errors: string[] = [];
  const [buckets, evalComputed, structural, severe, divResolved, termsCov, ranksFinal] = await Promise.all([
    bucketsMaterialized(planVersionId),
    evaluationComputed(planVersionId),
    structuralErrorsAbsent(planVersionId),
    severeViolationsPresent(planVersionId),
    divergenceResolved(planVersionId),
    termsCoverage(planVersionId),
    ranksAreFinal(planVersionId),
  ]);

  const relaxLegacy = options?.relaxBucketsAndEvaluationForLegacy === true;
  const evalOk = evalComputed.value;
  const bucketsOkOrRelaxed = buckets.value || relaxLegacy;
  const evalOkOrRelaxed = evalOk || relaxLegacy;
  const structuralOk = structural.value;
  const severePresent = severe.value;
  const divOk = divResolved.value;

  let overridesOk = true;
  if (severePresent) {
    const supabase = getSupabaseClient();
    const { data: evalRow } = await supabase
      .from(TABLE_PLAN_CONSTRAINT_EVALUATIONS)
      .select('id')
      .eq('plan_version_id', planVersionId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    const evaluationId = evalRow?.id ?? null;
    const overridesResult = await overridesApproved(planVersionId, { evaluationId: evaluationId ?? undefined });
    overridesOk = overridesResult.value;
    if (!overridesOk) {
      // Fix first, rule second (build contract §2.8): name what is wrong and
      // on which item before saying what the law demands.
      const base = overridesResult.reason ?? 'Severe violations present and no approved overrides';
      const what = describeSevereChecks(severe.severeChecks);
      errors.push(what ? `${what}. ${SEVERE_OVERRIDE_RULE}` : base);
    }
  }

  let divOkOrOverride = divOk;
  if (!divOk) {
    const overrideDiv = await overrideForUnresolvedDivergence(planVersionId);
    divOkOrOverride = overrideDiv.value;
    if (!divOkOrOverride) errors.push(overrideDiv.reason ?? 'Divergence not resolved and no governance override');
  }

  let termsOkOrOverride = termsCov.value;
  if (!termsOkOrOverride) {
    const overrideTerms = await overrideForTermsCoverage(planVersionId, termsCov.uncoveredItemIds);
    termsOkOrOverride = overrideTerms.value;
    if (!termsOkOrOverride) errors.push(termsCov.reason ?? 'Commitments without stop conditions (R-1)');
  }

  const blockers: PorBlocker[] = [];

  if (!bucketsOkOrRelaxed) {
    errors.push(buckets.reason ?? 'Buckets not materialized');
    blockers.push({
      blocker_code: 'BUCKETS_NOT_MATERIALIZED',
      title: 'Capacity buckets not materialized',
      remediation_route: '/planning/engine/capacity',
    });
  }
  if (!evalOkOrRelaxed) {
    errors.push(evalComputed.reason ?? 'Evaluation not computed');
    blockers.push({
      blocker_code: 'EVALUATION_NOT_COMPUTED',
      title: 'Constraint evaluation has not been run',
      remediation_route: '/planning/plans',
    });
  }
  if (!structuralOk) {
    errors.push(structural.reason ?? 'Structural errors present');
    blockers.push({
      blocker_code: 'STRUCTURAL_ERRORS_PRESENT',
      title: 'Structural constraint errors must be resolved',
      remediation_route: '/planning/plans',
    });
  }
  if (severePresent && !overridesOk) {
    const what = describeSevereChecks(severe.severeChecks);
    blockers.push({
      blocker_code: 'SEVERE_VIOLATIONS_UNOVERRIDDEN',
      title: what ? `${what}. ${SEVERE_OVERRIDE_RULE}` : 'Severe violations require a governance override',
      remediation_route: '/planning/plans',
      severe_checks: severe.severeChecks,
    });
  }
  if (!divOkOrOverride) {
    blockers.push({
      blocker_code: 'DIVERGENCES_UNRESOLVED',
      title: 'Settle each divergence below: say why it is accepted or how it was mitigated',
    });
  }
  if (!termsOkOrOverride) {
    blockers.push({
      blocker_code: 'COMMITMENTS_WITHOUT_TERMS',
      title: 'Commitments carry no stop condition — attach terms (or record a governance override)',
      count: termsCov.uncoveredItemIds.length,
      remediation_route: termsCov.planCycleId
        ? `/planning/plans/${termsCov.planCycleId}/terms`
        : '/planning/plans',
    });
  }

  if (!ranksFinal.value) {
    errors.push(ranksFinal.reason ?? 'Ranks are not final');
    blockers.push({
      blocker_code: 'RANKS_NOT_FINAL',
      title: ranksFinal.reason ?? 'Two items share a rank, or an item in the plan has none',
      count: countItemsWithRankProblems(ranksFinal.problems),
      remediation_route: '/planning/plans',
    });
  }

  const bucketsPass = bucketsOkOrRelaxed;
  const evalPass = evalOkOrRelaxed;
  const truthTableAllowed = bucketsPass && evalPass && structuralOk && divOkOrOverride && termsOkOrOverride && ranksFinal.value && !(severePresent && !overridesOk);

  const result: PorAuthorizableResult = {
    ready: truthTableAllowed,
    blockers,
    allowed: truthTableAllowed,
    errors,
    predicates: options?.includePredicates
      ? {
          bucketsMaterialized: buckets.value,
          evaluationComputed: evalOk,
          structuralErrorsAbsent: structuralOk,
          severeViolationsPresent: severePresent,
          overridesApproved: overridesOk,
          divergenceResolved: divOkOrOverride,
          termsCoverage: termsOkOrOverride,
        }
      : undefined,
  };
  return result;
}

/**
 * ReprioritizationRecommended(plan_cycle) — Doc 15 §5.1.
 * True iff at least one reprioritization_trigger exists for the cycle with status indicating recommendation.
 */
export async function reprioritizationRecommended(planCycleId: string): Promise<PredicateResult> {
  try {
    const supabase = getSupabaseClient();
    const { count, error } = await supabase
      .from(TABLE_REPRIORITIZATION_TRIGGERS)
      .select('id', { count: 'exact', head: true })
      .eq('plan_cycle_id', planCycleId);
    if (error) return { value: false, reason: error.message };
    return { value: (count ?? 0) > 0 };
  } catch (e) {
    return { value: false, reason: e instanceof Error ? e.message : 'Failed to check reprioritization' };
  }
}
